/**
 * Customer reviews carousel (Smooth Sales) — behaviour for
 * blocks/customer-reviews.liquid.
 *
 * The carousel already works without this module: the track is a native
 * horizontal scroller with scroll-snap, so it swipes on touch and scrolls with
 * a trackpad. This adds what native scrolling cannot:
 *  - previous/next arrows that page one full view at a time, disabled at
 *    either end, and hidden entirely when every review already fits;
 *  - "Read more" on reviews clamped to eight lines, shown only where the text
 *    actually overflows;
 *  - in the theme editor, selecting a review block scrolls it into view, so
 *    editing the seventh review does not mean hunting for it.
 *
 * Loaded as a plain (deferred) module from the block, so the element is never
 * defined before the parser has emitted its children.
 */

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class CustomerReviewsCarousel extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #scrollFrame = 0;

  connectedCallback() {
    const track = this.querySelector('[data-reviews-track]');
    if (!track) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const previous = this.querySelector('[data-reviews-prev]');
    const next = this.querySelector('[data-reviews-next]');

    const updateArrows = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      this.toggleAttribute('data-scrollable', maxScroll > 2);
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
    };

    const pageBy = (direction) => {
      const card = track.querySelector('[data-review-card]');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const stride = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
      const cardsPerView = Math.max(1, Math.floor((track.clientWidth + gap) / stride));
      track.scrollBy({
        left: direction * stride * cardsPerView,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    };

    previous?.addEventListener('click', () => pageBy(-1), { signal });
    next?.addEventListener('click', () => pageBy(1), { signal });

    track.addEventListener(
      'scroll',
      () => {
        if (this.#scrollFrame) return;
        this.#scrollFrame = requestAnimationFrame(() => {
          this.#scrollFrame = 0;
          updateArrows();
        });
      },
      { passive: true, signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-review-more]') : null;
        if (!button || !this.contains(button)) return;
        const card = button.closest('[data-review-card]');
        if (!card) return;
        const expanded = card.toggleAttribute('data-expanded');
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? 'Show less' : 'Read more';
        updateArrows();
      },
      { signal }
    );

    // Theme editor only. The event is listened for on the document because
    // whether it bubbles from the block element is an editor detail we should
    // not depend on; the containment check keeps it to this carousel.
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target || !this.contains(target)) return;
        const card = target.closest('[data-review-card]');
        card?.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      },
      { signal }
    );

    // Card widths, and therefore what overflows, change with the viewport and
    // with the details column, so re-measure whenever the track resizes.
    this.#resizeObserver = new ResizeObserver(() => {
      updateArrows();
      this.#syncReadMore();
    });
    this.#resizeObserver.observe(track);

    if (previous) previous.hidden = false;
    if (next) next.hidden = false;
    updateArrows();
    this.#syncReadMore();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = 0;
  }

  /**
   * Show "Read more" only on reviews the clamp actually cuts off. The 2px
   * allowance absorbs sub-pixel line-height rounding, which otherwise reports
   * a review that fits exactly as overflowing by a fraction of a pixel.
   */
  #syncReadMore() {
    for (const card of this.querySelectorAll('[data-review-card]')) {
      const body = card.querySelector('[data-review-body]');
      const button = card.querySelector('[data-review-more]');
      if (!body || !button) continue;
      button.hidden = !card.hasAttribute('data-expanded') && body.scrollHeight <= body.clientHeight + 2;
    }
  }
}

if (!customElements.get('customer-reviews-carousel')) {
  customElements.define('customer-reviews-carousel', CustomerReviewsCarousel);
}
